import React, { useState, useEffect } from 'react';
import {
  TreePine,
  Sparkles,
  FileCheck2,
  ShieldCheck,
  BadgeCheck,
  Building,
  Briefcase,
  FileText,
  Scale,
  Award,
  Eye,
  X,
  ExternalLink,
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { fetchPublicContent, fetchPublicSettings, pickCmsShort, makeSectionCopyPicker } from '../services/publicContentService';

const FALLBACK_ACCRED_LOGOS = [
  { src: '/accreditations/aramco.png', name: 'أرامكو السعودية', nameEn: 'Saudi Aramco' },
  { src: '/accreditations/sabic.png', name: 'سابك', nameEn: 'SABIC' },
  { src: '/accreditations/neom.png', name: 'نيوم', nameEn: 'NEOM' },
  { src: '/accreditations/amaala.png', name: 'أمالا', nameEn: 'AMAALA' },
  { src: '/accreditations/royal-commission.png', name: 'الهيئة الملكية للجبيل وينبع', nameEn: 'Royal Commission for Jubail & Yanbu' },
  { src: '/accreditations/modon.png', name: 'مدن', nameEn: 'MODON' },
  { src: '/accreditations/moh.png', name: 'وزارة الصحة', nameEn: 'Ministry of Health' },
  { src: '/accreditations/ministry-industry.png', name: 'وزارة الصناعة والثروة المعدنية', nameEn: 'Ministry of Industry & Mineral Resources' },
  { src: '/accreditations/mewa.png', name: 'وزارة البيئة والمياه والزراعة', nameEn: 'Ministry of Environment, Water & Agriculture' },
  { src: '/accreditations/ministry-energy.png', name: 'وزارة الطاقة', nameEn: 'Ministry of Energy' },
  { src: '/accreditations/ministry-commerce.png', name: 'وزارة التجارة', nameEn: 'Ministry of Commerce' },
  { src: '/accreditations/saudi-electricity.png', name: 'الشركة السعودية للكهرباء', nameEn: 'Saudi Electricity Company' },
];

const FALLBACK_LEGAL_DOCS = [
  {
    icon: FileCheck2,
    title: 'تجديد تسجيل شركة التوازن البيئي لإجراء دراسات تقييم الأثر البيئي (EIA) في مدينة ينبع الصناعية (YIC)',
    titleEn: 'Renewal of Al-Tawazun Environmental Company\'s registration to conduct Environmental Impact Assessments (EIA) in Yanbu Industrial City (YIC)',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/vrq91id5_%D8%AA%D8%AC%D8%AF%D9%8A%D8%AF%20%D8%AA%D8%B3%D8%AC%D9%8A%D9%84%20%D8%B4%D8%B1%D9%83%D8%A9%20%D8%A7%D9%84%D8%AA%D9%88%D8%A7%D8%B2%D9%86%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%20%D9%84%D8%A5%D8%AC%D8%B1%D8%A7%D8%A1%20%D8%AF%D8%B1%D8%A7%D8%B3%D8%A7%D8%AA%20%D8%AA%D9%82%D9%8A%D9%8A%D9%85%20%D8%A7%D9%84%D8%A3%D8%AB%D8%B1%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%20%28EIA%29%20%D9%81%D9%8A%20%D9%85%D8%AF%D9%8A%D9%86%D8%A9%20%D9%8A%D9%86%D8%A8%D8%B9%20%D8%A7%D9%84%D8%B5%D9%86%D8%A7%D8%B9%D9%8A%D8%A9%20%28YIC%29..pdf',
  },
  {
    icon: ShieldCheck,
    title: 'شهادة الامتثال لأمن المعلومات',
    titleEn: 'Information Security Compliance Certificate',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/jc1yo4yw_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D8%A7%D9%84%D8%A7%D9%85%D8%AA%D8%AB%D8%A7%D9%84%20%D9%84%D8%A3%D9%85%D9%86%20%D8%A7%D9%84%D9%85%D8%B9%D9%84%D9%88%D9%85%D8%A7%D8%AA.pdf',
  },
  {
    icon: BadgeCheck,
    title: 'الترخيص البيئي لرصد ومراقبة جودة الوسائط البيئية ومصادر التلوث',
    titleEn: 'Environmental permit for monitoring and controlling the quality of environmental media and sources of pollution',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/hjzntghb_%D8%A7%D9%84%D8%AA%D8%B1%D8%AE%D9%8A%D8%B5%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%20%D9%84%D8%B1%D8%B5%D8%AF%20%D9%88%D9%85%D8%B1%D8%A7%D9%82%D8%A8%D8%A9%20%D8%AC%D9%88%D8%AF%D8%A9%20%D8%A7%D9%84%D9%88%D8%B3%D8%A7%D8%A6%D8%B7%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%D8%A9%20%D9%88%D9%85%D8%B5%D8%A7%D8%AF%D8%B1%20%D8%A7%D9%84%D8%AA%D9%84%D9%88%D8%AB.pdf',
  },
  {
    icon: ShieldCheck,
    title: 'الترخيص البيئي لخدمات الدراسات والاستشارات البيئية',
    titleEn: 'Environmental License for Environmental Studies and Consultation Services',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/zczpc15e_%D8%A7%D9%84%D8%AA%D8%B1%D8%AE%D9%8A%D8%B5%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%20%D9%84%D8%AE%D8%AF%D9%85%D8%A7%D8%AA%20%D8%A7%D9%84%D8%AF%D8%B1%D8%A7%D8%B3%D8%A7%D8%AA%20%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D8%A7%D8%AA%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%D8%A9.pdf',
  },
  {
    icon: FileText,
    title: 'شهادة السجل التجاري',
    titleEn: 'Commercial Registration Certificate',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/tgzbkvdp_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D8%A7%D9%84%D8%B3%D8%AC%D9%84%20%D8%A7%D9%84%D8%AA%D8%AC%D8%A7%D8%B1%D9%8A.pdf',
  },
  {
    icon: Building,
    title: 'شهادة إشتراك كعضو لدى الغرفة التجارية',
    titleEn: 'Chamber of Commerce Membership Certificate',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/omnv4ilt_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D8%A5%D8%B4%D8%AA%D8%B1%D8%A7%D9%83%20%D9%83%D8%B9%D8%B6%D9%88%20%D9%84%D8%AF%D9%89%20%D8%A7%D9%84%D8%BA%D8%B1%D9%81%D8%A9%20%D8%A7%D9%84%D8%AA%D8%AC%D8%A7%D8%B1%D9%8A%D8%A9.pdf',
  },
  {
    icon: Award,
    title: 'تسجيل الموافقة البيئية للهيئة الملكية في جيزان',
    titleEn: 'Royal Commission Environmental Approval Registration in Jazan',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/3johu1lr_%D8%AA%D8%B3%D8%AC%D9%8A%D9%84%20%D8%A7%D9%84%D9%85%D9%88%D8%A7%D9%81%D9%82%D8%A9%20%D8%A7%D9%84%D8%A8%D9%8A%D8%A6%D9%8A%D8%A9%20%D9%84%D9%84%D9%87%D9%8A%D8%A6%D8%A9%20%D8%A7%D9%84%D9%85%D9%84%D9%83%D9%8A%D8%A9%20%D9%81%D9%8A%20%D8%AC%D9%8A%D8%B2%D8%A7%D9%86.pdf',
  },
  {
    icon: Scale,
    title: 'شهادة هيئة الزكاة والضريبة والجمارك',
    titleEn: 'Zakat, Tax and Customs Authority (ZATCA) Certificate',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/o0nkgx6p_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D9%87%D9%8A%D8%A6%D8%A9%20%D8%A7%D9%84%D8%B2%D9%83%D8%A7%D8%A9%20%D9%88%D8%A7%D9%84%D8%B6%D8%B1%D9%8A%D8%A8%D8%A9%20%D9%88%D8%A7%D9%84%D8%AC%D9%85%D8%A7%D8%B1%D9%83.pdf',
  },
  {
    icon: Sparkles,
    title: 'شهادة تأهيل استشاري لدى هيئة المدن والمناطق الاقتصادية الخاصة',
    titleEn: 'Consultant Qualification Certificate from the Economic Cities and Special Zones Authority (ECZA)',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/oyur0w8z_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D8%AA%D8%A3%D9%87%D9%8A%D9%84%20%D8%A7%D8%B3%D8%AA%D8%B4%D8%A7%D8%B1%D9%8A%20%D9%84%D8%AF%D9%89%20%D9%87%D9%8A%D8%A6%D8%A9%20%D8%A7%D9%84%D9%85%D8%AF%D9%86%20%D9%88%D8%A7%D9%84%D9%85%D9%86%D8%A7%D8%B7%D9%82%20%D8%A7%D9%84%D8%A7%D9%82%D8%AA%D8%B5%D8%A7%D8%AF%D9%8A%D8%A9%20%D8%A7%D9%84%D8%AE%D8%A7%D8%B5%D8%A9.pdf',
  },
  {
    icon: Briefcase,
    title: 'شهادة مزاولة المهنة لدى وزارة التجارة والاستثمار',
    titleEn: 'Professional Practice Certificate from the Ministry of Commerce and Investment',
    pdf: 'https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/j2mte00e_%D8%B4%D9%87%D8%A7%D8%AF%D8%A9%20%D9%85%D8%B2%D8%A7%D9%88%D9%84%D8%A9%20%D8%A7%D9%84%D9%85%D9%87%D9%86%D8%A9%20%D9%84%D8%AF%D9%89%20%D9%88%D8%B2%D8%A7%D8%B1%D8%A9%20%D8%A7%D9%84%D8%AA%D8%AC%D8%A7%D8%B1%D8%A9%20%D9%88%D8%A7%D9%84%D8%A7%D8%B3%D8%AA%D8%AB%D9%85%D8%A7%D8%B1.pdf',
  },
];

const AccreditationsPage = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [activePdf, setActivePdf] = useState(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Icons we rotate through when the admin adds a new certificate (they only
  // upload title + PDF; the visual icon is chosen from this pool by index).
  const ICON_POOL = [FileCheck2, ShieldCheck, BadgeCheck, FileText, Building, Award, Scale, Sparkles, Briefcase];

  // Legal docs list — pulled from the CMS when available, otherwise falls back
  // to the original static list so the page never appears empty.
  const [legalDocs, setLegalDocs] = useState(FALLBACK_LEGAL_DOCS);

  // Client / accreditation logos grid — CMS-driven with static fallback.
  const [accredLogos, setAccredLogos] = useState(FALLBACK_ACCRED_LOGOS);

  // Optional CMS-driven subtitle under the "Our Legal Entity" heading.
  const [legalIntro, setLegalIntro] = useState(null); // { ar, en } | null

  // Accreditations page hero (Phase B)
  const [accHero, setAccHero] = useState({});
  const [sc, setSc] = useState({});

  useEffect(() => {
    let alive = true;
    fetchPublicContent('certificates').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;
      const sorted = [...items].sort((a, b) => (a.order || 0) - (b.order || 0));
      const mapped = sorted
        .filter((it) => it.pdf)
        .map((it, idx) => ({
          icon: ICON_POOL[idx % ICON_POOL.length],
          title:   it.title_ar || it.title_en || '',
          titleEn: it.title_en || it.title_ar || '',
          pdf:     it.pdf,
        }));
      if (mapped.length > 0) setLegalDocs(mapped);
    });
    fetchPublicContent('client-logos').then((items) => {
      if (!alive || !Array.isArray(items) || items.length === 0) return;
      const mapped = items
        .filter((it) => !!it.image)
        .map((it) => ({
          src:    it.image,
          name:   it.title_ar || it.title_en || '',
          nameEn: it.title_en || it.title_ar || '',
        }));
      if (mapped.length > 0) setAccredLogos(mapped);
    });
    fetchPublicSettings('company_info').then((v) => {
      if (!alive) return;
      const val = v && v.legal;
      if (val && (val.ar || val.en)) setLegalIntro(val);
    });
    fetchPublicSettings('accreditations_hero').then((v) => {
      if (alive) setAccHero(v || {});
    });
    fetchPublicSettings('section_copy').then((v) => {
      if (alive) setSc(v || {});
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sec = makeSectionCopyPicker(sc, isAr);

  // Lock body scroll & ESC-to-close
  useEffect(() => {
    if (!activePdf) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setActivePdf(null); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [activePdf]);

  // Fetch PDF as Blob (the remote CDN sends X-Frame-Options: SAMEORIGIN which blocks direct iframe embeds)
  useEffect(() => {
    if (!activePdf) {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setPdfError(false);
      setPdfLoading(false);
      return;
    }
    let cancelled = false;
    let createdUrl = null;
    setPdfLoading(true);
    setPdfError(false);

    fetch(activePdf.url)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
        setPdfLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setPdfError(true);
        setPdfLoading(false);
      });

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePdf]);

  return (
    <div dir={isAr ? 'rtl' : 'ltr'}>
      <div className="relative">
        {/* Forest / tree theme — matches the Field Solutions page.
            Deep green ambient glows on a light (or emerald-950) surface. */}
        <div className="fixed inset-0 -z-10 bg-white dark:bg-[#071810]" />

        {/* Subtle ambient glows */}
        <div className="fixed top-[10%] left-[8%] w-[500px] h-[500px] rounded-full blur-[110px] bg-brand-green/8 dark:bg-brand-green/15 pointer-events-none -z-10" />
        <div className="fixed top-[45%] right-[5%] w-[520px] h-[520px] rounded-full blur-[110px] bg-emerald-400/8 dark:bg-emerald-500/12 pointer-events-none -z-10" />
        <div className="fixed bottom-[15%] left-[30%] w-[420px] h-[420px] rounded-full blur-[110px] bg-brand-green-100/40 dark:bg-brand-green/10 pointer-events-none -z-10" />

        {/* IMMERSIVE MOUNTAIN VALLEY HERO */}
        <section className="relative h-[100vh] min-h-[680px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://customer-assets.emergentagent.com/job_ebc-depths/artifacts/htjgdemg_Environmental%20fieldwork%20in%20a%20mountainous%20valley.png')",
              backgroundPosition: 'center 35%',
            }}
          />

          {/* Atmospheric overlays */}
          <div className="absolute inset-0 bg-gradient-to-b from-green-950/30 via-emerald-950/40 to-green-950/75" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-transparent to-emerald-950/30" />

          {/* God-rays */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="absolute top-0 origin-top"
                style={{
                  left: `${10 + i * 16}%`,
                  width: '180px',
                  height: '120%',
                  background:
                    'linear-gradient(to bottom, rgba(255,240,180,0.18), rgba(255,240,180,0.06) 40%, transparent 80%)',
                  transform: `rotate(${4 + i * 1.5}deg)`,
                  filter: 'blur(8px)',
                  animation: `ray-shimmer ${8 + i * 2}s ease-in-out infinite`,
                  animationDelay: `${i * 0.7}s`,
                }}
              />
            ))}
          </div>

          {/* Floating leaf particles */}
          <div className="absolute inset-0 pointer-events-none">
            {[...Array(25)].map((_, i) => (
              <div
                key={i}
                className="absolute rounded-full"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  width: `${3 + Math.random() * 4}px`,
                  height: `${3 + Math.random() * 4}px`,
                  background: `rgba(${100 + Math.random() * 80},${160 + Math.random() * 60},${100 + Math.random() * 50},${0.35 + Math.random() * 0.4})`,
                  animation: `leaf-fall ${12 + Math.random() * 10}s linear infinite`,
                  animationDelay: `${Math.random() * 12}s`,
                  filter: 'blur(0.5px)',
                }}
              />
            ))}
          </div>

          {/* Hero copy */}
          <div className="relative h-full flex items-center justify-center container mx-auto px-4">
            <div className="text-center max-w-4xl animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-white/10 border border-emerald-200/40 mb-8 backdrop-blur-md">
                <TreePine className="w-4 h-4 text-emerald-200" />
                <span
                  className="text-xs tracking-[0.4em] uppercase text-emerald-100 font-semibold"
                  data-testid="acc-hero-eyebrow"
                >
                  {pickCmsShort(accHero.eyebrow, isAr) || (isAr ? 'مركز الثقة' : 'Trust Center')}
                </span>
              </div>

              <h1
                className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 text-white leading-[1.05]"
                style={{ textShadow: '0 4px 30px rgba(0,0,0,0.7), 0 0 60px rgba(2,117,60,0.4)' }}
                data-testid="acc-hero-title"
              >
                {pickCmsShort(accHero.title_line1, isAr) || (isAr ? 'مركز' : 'Accreditations')}
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-200 via-emerald-300 to-brand-green-400">
                  {pickCmsShort(accHero.title_line2, isAr) || (isAr ? 'الاعتمادات' : 'Center')}
                </span>
              </h1>

              <p
                className="text-lg md:text-xl text-emerald-50/95 max-w-2xl mx-auto leading-loose font-light"
                style={{ textShadow: '0 2px 12px rgba(0,0,0,0.6)' }}
                data-testid="acc-hero-subtitle"
              >
                {pickCmsShort(accHero.subtitle, isAr) || (isAr
                  ? 'اعتماداتنا الرسمية وكياننا القانوني — أساس الثقة الذي نبني عليه كل مشروع.'
                  : 'Our official accreditations and legal foundation — the trust on which every project is built.')}
              </p>
            </div>
          </div>
        </section>

        {/* Smooth transition into content — same as Field Solutions */}
        <div className="relative bg-gradient-to-b from-green-950 via-emerald-50 to-white dark:via-[#0a2014] dark:to-[#071810] h-32" />

        {/* Section 1: اعتماداتنا — Forest styled */}
        <section id="accreditations-clients" className="relative py-20 bg-white dark:bg-[#071810]">
          <div className="relative container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center mb-12 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-green/10 dark:bg-brand-green/15 border border-brand-green/30 mb-5 backdrop-blur-sm">
                <Sparkles className="w-4 h-4 text-brand-green-700 dark:text-brand-green-300" />
                <span className="text-xs tracking-[0.3em] uppercase text-brand-green-800 dark:text-brand-green-200" data-testid="acc-section1-eyebrow">
                  {sec('acc_section1_eyebrow', isAr ? 'الموثوقية' : 'Trust')}
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white" data-testid="acc-section1-heading">
                {sec('acc_section1_heading', isAr ? 'اعتماداتنا' : 'Our Accreditations')}
              </h2>
              <div className={`space-y-5 text-slate-700 dark:text-slate-300 text-base md:text-lg leading-loose ${isAr ? 'text-right' : 'text-left'}`}>
                <p data-testid="acc-section1-body-1">
                  {sec('acc_section1_body_p1', isAr
                    ? 'نفخر كوننا شريكاً موثوقاً ومعتمداً لدى أبرز الجهات والمؤسسات الصناعية الكبرى في المملكة، مما يعكس كفاءتنا الفنية والتزامنا بأعلى معايير الامتثال البيئي.'
                    : 'We take pride in being a trusted and accredited partner to the Kingdom\'s leading entities and major industrial institutions, reflecting our technical competence and commitment to the highest standards of environmental compliance.')}
                </p>
                <p data-testid="acc-section1-body-2">
                  {sec('acc_section1_body_p2', isAr
                    ? 'شركتنا مرخصة ومصنفة من قبل المركز الوطني للرقابة على الالتزام البيئي لتقديم الحلول المتكاملة في المجالات البيئية.'
                    : 'Our company is licensed and classified by the National Center for Environmental Compliance to provide integrated environmental solutions.')}
                </p>
              </div>
            </div>

            <p className={`max-w-4xl mx-auto mb-10 text-slate-700 dark:text-slate-300 text-base md:text-lg leading-loose ${isAr ? 'text-right' : 'text-left'}`} data-testid="acc-section1-caption">
              {sec('acc_section1_caption', isAr
                ? 'كما نمتلك سجلًا حافلًا من الاعتمادات الرسمية لدى الجهات والوزارات والشركات الحكومية التالية على سبيل المثال:'
                : 'We also possess a proven track record of official accreditations from the following ministries, government agencies, and corporations, for instance:')}
            </p>

            {/* Logo grid - forest styled */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {accredLogos.map((logo, i) => (
                <div
                  key={i}
                  data-testid={`accred-logo-${i}`}
                  className="group relative animate-fade-in-up"
                  style={{ animationDelay: `${i * 0.06}s` }}
                >
                  <div className="relative h-48 rounded-2xl bg-white dark:bg-emerald-950/40
                                border border-slate-200 dark:border-emerald-700/30
                                hover:border-brand-green/50
                                hover:shadow-[0_15px_40px_-15px_rgba(2,117,60,0.4)]
                                transition-all duration-500 hover:-translate-y-1
                                p-6 flex flex-col items-center justify-center gap-4">
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-green to-emerald-600 opacity-80" />
                    <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-brand-green to-emerald-600
                                  opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500 pointer-events-none" />

                    <div className="relative h-20 w-full flex items-center justify-center">
                      <img
                        src={logo.src}
                        alt={isAr ? logo.name : logo.nameEn}
                        className="max-h-full max-w-full object-contain transition-transform duration-500 group-hover:scale-110"
                      />
                    </div>
                    <div className="relative text-center">
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 leading-snug">
                        {isAr ? logo.name : logo.nameEn}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2: كياننا القانوني — Forest styled */}
        <section id="legal-entity" className="relative py-20 bg-white dark:bg-[#071810]">
          <div className="relative container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center mb-14 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-green/10 dark:bg-brand-green/15 border border-brand-green/30 mb-5 backdrop-blur-sm">
                <Scale className="w-4 h-4 text-brand-green-700 dark:text-brand-green-300" />
                <span className="text-xs tracking-[0.3em] uppercase text-brand-green-800 dark:text-brand-green-200" data-testid="acc-section2-eyebrow">
                  {sec('acc_section2_eyebrow', isAr ? 'الكيان القانوني' : 'Legal Entity')}
                </span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold mb-4 text-slate-900 dark:text-white" data-testid="acc-section2-heading">
                {sec('acc_section2_heading', isAr ? 'كياننـا القانوني' : 'Our Legal Entity')}
              </h2>
              <p className="text-base md:text-lg text-slate-600 dark:text-slate-400" data-testid="legal-intro">
                {(legalIntro && (isAr ? legalIntro.ar : legalIntro.en)) || (isAr ? 'شركة التوازن البيئي' : 'Environmental Balance Co.')}
              </p>
            </div>

            <div className="max-w-5xl mx-auto space-y-5">
              {legalDocs.map((doc, i) => {
                const Icon = doc.icon;
                return (
                  <article
                    key={i}
                    data-testid={`legal-doc-${i}`}
                    className="group relative bg-white dark:bg-emerald-950/40 border border-slate-200 dark:border-emerald-700/30
                             rounded-2xl p-6 lg:p-8
                             hover:border-brand-green/50 hover:shadow-[0_15px_40px_-15px_rgba(2,117,60,0.4)]
                             transition-all duration-500 hover:-translate-y-0.5
                             animate-fade-in-up overflow-hidden"
                    style={{ animationDelay: `${i * 0.06}s` }}
                  >
                    {/* Top accent bar (matches ForestCard) */}
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-brand-green to-emerald-600 opacity-80" />
                    {/* Hover halo */}
                    <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-brand-green to-emerald-600
                                    opacity-0 group-hover:opacity-[0.08] blur-2xl transition-opacity duration-500 pointer-events-none" />
                    {/* Side rail on the leading edge */}
                    <div className={`absolute top-0 ${isAr ? 'right-0 rounded-r-2xl' : 'left-0 rounded-l-2xl'} w-1.5 h-full bg-gradient-to-b from-brand-green-400 via-brand-green to-emerald-700 opacity-80`} />

                    <div className={`relative flex items-start gap-5 ${isAr ? 'flex-row-reverse text-right' : 'text-left'}`}>
                      <div className="flex-shrink-0">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-green to-emerald-600
                                      flex items-center justify-center shadow-md
                                      transition-transform duration-500 group-hover:scale-110 group-hover:rotate-3">
                          <Icon className="w-7 h-7 text-white" />
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className={`flex items-center justify-between gap-3 mb-3 ${isAr ? 'flex-row-reverse' : ''}`}>
                          <h3 className="text-lg md:text-xl font-bold text-slate-900 dark:text-white leading-snug">
                            {isAr ? doc.title : doc.titleEn}
                          </h3>
                          <span className="text-xs font-mono tracking-[0.25em] text-brand-green-700/70 dark:text-brand-green-300/70 flex-shrink-0">
                            {String(i + 1).padStart(2, '0')}
                          </span>
                        </div>

                        <button
                          type="button"
                          data-testid={`view-certificate-${i}`}
                          onClick={() => setActivePdf({ url: doc.pdf, title: isAr ? doc.title : doc.titleEn })}
                          className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl
                                    bg-gradient-to-r from-brand-green to-emerald-600
                                    hover:from-emerald-600 hover:to-brand-green
                                    text-white text-sm font-semibold shadow-md
                                    hover:shadow-lg hover:-translate-y-0.5 transition-all
                                    ${isAr ? 'flex-row-reverse' : ''}`}
                        >
                          <Eye className="w-4 h-4" />
                          {isAr ? 'عرض الشهادة' : 'View Certificate'}
                        </button>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <div className="h-20 bg-white dark:bg-[#071810]" />
      </div>

      {/* ===== PDF LIGHTBOX MODAL ===== */}
      {activePdf && (
        <div
          data-testid="pdf-lightbox"
          dir={isAr ? 'rtl' : 'ltr'}
          className="fixed inset-0 z-[100] flex flex-col bg-black/80 backdrop-blur-sm animate-fade-in-up"
          onClick={(e) => { if (e.target === e.currentTarget) setActivePdf(null); }}
        >
          {/* Toolbar */}
          <div className="flex-shrink-0 flex items-center gap-3 px-4 sm:px-6 py-3 bg-slate-900/95 text-white border-b border-white/10">
            <h4 className={`flex-1 text-sm sm:text-base font-semibold truncate ${isAr ? 'text-right' : 'text-left'}`}>
              {activePdf.title}
            </h4>
            <a
              href={activePdf.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pdf-open-new-tab"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-green hover:bg-emerald-600 text-white text-xs sm:text-sm font-semibold transition-colors ${isAr ? 'flex-row-reverse' : ''}`}
            >
              <ExternalLink className="w-4 h-4" />
              <span className="hidden sm:inline">{isAr ? 'فتح في تبويب جديد' : 'Open in new tab'}</span>
            </a>
            <button
              type="button"
              onClick={() => setActivePdf(null)}
              data-testid="pdf-close-button"
              aria-label={isAr ? 'إغلاق' : 'Close'}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* PDF viewer */}
          <div className="flex-1 bg-slate-800 overflow-hidden relative">
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white">
                <div className="w-12 h-12 border-4 border-white/20 border-t-brand-green rounded-full animate-spin" />
                <p className="text-sm text-white/80">
                  {isAr ? 'جارٍ تحميل الشهادة…' : 'Loading certificate…'}
                </p>
              </div>
            )}
            {pdfError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-white p-6 text-center">
                <p className="text-sm sm:text-base text-white/90 max-w-md">
                  {isAr
                    ? 'تعذّر عرض الشهادة داخل الصفحة. يرجى فتحها في تبويب جديد.'
                    : 'Unable to display the certificate inline. Please open it in a new tab.'}
                </p>
                <a
                  href={activePdf.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-green hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  {isAr ? 'فتح في تبويب جديد' : 'Open in new tab'}
                </a>
              </div>
            )}
            {blobUrl && !pdfError && (
              <iframe
                key={blobUrl}
                src={blobUrl}
                title={activePdf.title}
                className="w-full h-full border-0 bg-white"
              />
            )}
          </div>

          {/* Mobile fallback hint */}
          <div className="flex-shrink-0 px-4 py-2 bg-slate-900/95 text-white/70 text-xs text-center sm:hidden">
            {isAr
              ? 'إذا واجهت صعوبة في التصفح، استخدم زر "فتح في تبويب جديد".'
              : 'For best viewing on mobile, tap "Open in new tab".'}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccreditationsPage;
